<template>
  <div class="grid sm:grid-cols-2 gap-2 sm:gap-4 items-start">
    <!-- Vehicle Details -->
    <Card style="min-height: 82px">
      <CardBody>
        <div class="relative">
          <div class="left-0 top-0 right-0 flex justify-between flex-col lg:flex-row items-center">
            <div class="flex items-start flex-grow">
              <img
                :src="agreementData.vehicle.manufacturerLogoUrl"
                class="mt-1"
                width="30"
              />
              <div class="ml-3">
                <div class="font-semibold text-secondary flex items-center gap-2">
                  {{ agreementData.vehicle.model }}
                  <Icon
                    v-if="agreementData.vehicle.fuelType == 'electric'"
                    v-tippy="'Electric'"
                    type="bolt"
                  />
                  <Icon
                    v-if="agreementData.vehicle.fuelType == 'hybrid'"
                    v-tippy="'Hybrid'"
                    type="leaf"
                  />
                  <Icon
                    v-if="agreementData.vehicle.fuelType == 'petrol'"
                    v-tippy="'Petrol'"
                    type="gas-pump"
                  />
                  <Icon
                    v-if="agreementData.vehicle.fuelType == 'diesel'"
                    v-tippy="'Diesel'"
                    type="gas-pump"
                  />
                  <Chip
                    v-if="agreementData.newUsed"
                    size="sm"
                  >
                    {{ agreementData.newUsed }}
                  </Chip>
                  <Chip
                    v-if="agreementData.vehicle.modelYear"
                    size="sm"
                  >
                    {{ agreementData.vehicle.modelYear }}
                  </Chip>
                </div>
                <div class="text-sm text-quaternary pr-4">
                  {{ agreementData.vehicle.derivative }}
                </div>
              </div>
            </div>
            <div class="flex items-center mt-5 lg:mt-0">
              <CopyText :explicit-text-to-copy="agreementData.vehicle.vrm">
                <LicensePlate
                  :plate="agreementData.vehicle.vrm"
                  country="uk"
                  :design="agreementData.vehicle.fuelType == 'electric' ? 'ev' : ''"
                  greyscale
                />
              </CopyText>
            </div>
          </div>

          <div class="flex justify-center -mt-[10px] -mb-[60px] mx-8">
            <img
              v-show="isVehicleImageLoaded"
              :src="agreementData.vehicle.imageUrl ?? undefined"
              @load="isVehicleImageLoaded = true"
            />
            <Skeleton
              v-if="!isVehicleImageLoaded"
              width="100%"
              height="15rem"
              class="mb-2"
            />
          </div>
        </div>
      </CardBody>
      <div
        class="bg-gradient-to-t from-transparent via-transparent to-bg-tertiary rounded-xl rounded-t-[2rem] p-6 -mt-[70px] pt-[70px]"
      >
        <MileageTracker
          :mileage-data="{
            unit: agreementData.odometer.unit === 'miles' ? 'mi' : 'km',
            atStart: agreementData.odometer.atStart ?? 0,
            current: agreementData.odometer.current ?? 0,
            expected: agreementData.odometer.expected ?? 0,
            atEnd: agreementData.odometer.allowanceTotal ?? 0
          }"
        />
        <div class="flex justify-center">
          <Button
            label="Update Mileage"
            icon="pencil"
            plain
            @click="$emit('open-update-mileage-modal')"
          />
        </div>
      </div>
    </Card>

    <!-- Finance Summary -->
    <div>
      <Card>
        <CardBody>
          <div>
            <TermTracker
              :start-date="agreementData.startDate"
              :end-date="agreementData.endDate"
            />
          </div>
        </CardBody>

        <!-- Details Grid -->
        <div class="border-y border-secondary grid grid-cols-1 lg:grid-cols-3 leading-6 gap-[1px] bg-quaternary">
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">Product</div>
            <div class="text-xl font-bold text-secondary">
              {{ $t('ui.entities.financeProducts.' + agreementData.productType) }}
            </div>
          </div>
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">Monthly Payment</div>
            <div class="text-xl font-bold text-secondary">
              {{ formatNumber(agreementData.monthlyRepayment, 'currency', 2, agreementData.currency) }}
            </div>
          </div>
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">Final Payment</div>
            <div class="text-xl font-bold text-secondary">
              {{
                agreementData.finalRepayment
                  ? formatNumber(agreementData.finalRepayment, 'currency', 2, agreementData.currency)
                  : '-'
              }}
            </div>
          </div>
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">APR</div>
            <div class="text-xl font-bold text-secondary">{{ agreementData.rate }}%</div>
          </div>
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">Term</div>
            <div class="text-xl font-bold text-secondary">{{ agreementData.term }}m</div>
          </div>
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">Deposit</div>
            <div class="text-xl font-bold text-secondary">
              {{ formatNumber(agreementData.deposit, 'currency', 2, agreementData.currency) }}
            </div>
          </div>
          <div
            v-if="showFullDetails"
            class="py-4 px-6 bg-primary-alt"
          >
            <div class="text-sm text-quaternary">Purchase Price</div>
            <div class="text-xl font-bold text-secondary">
              {{ formatNumber(agreementData.purchasePrice, 'currency', 0, agreementData.currency) }}
            </div>
          </div>
          <div
            v-if="showFullDetails"
            class="py-4 px-6 bg-primary-alt"
          >
            <div class="text-sm text-quaternary">Amount Financed</div>
            <div class="text-xl font-bold text-secondary">
              {{ formatNumber(agreementData.amountBorrowed, 'currency', 0, agreementData.currency) }}
            </div>
          </div>
          <div
            v-if="showFullDetails"
            class="py-4 px-6 bg-primary-alt"
          >
            <div class="text-sm text-quaternary">Distance Allowed</div>
            <div class="text-xl font-bold text-secondary">
              {{
                agreementData.odometer.allowanceYearly
                  ? formatAbbreviatedNumber(agreementData.odometer.allowanceYearly, 2, false) +
                    ' ' +
                    agreementData.odometer.unit
                  : '-'
              }}
            </div>
          </div>
          <div
            v-if="showFullDetails"
            class="py-4 px-6 bg-primary-alt"
          >
            <div class="text-sm text-quaternary">Reg Date</div>
            <div class="text-xl font-bold text-secondary">
              {{ agreementData.vehicle.regDate ? dayjs(agreementData.vehicle.regDate).format('DD/MM/YYYY') : '' }}
            </div>
          </div>
          <div
            v-if="showFullDetails"
            class="py-4 px-6 bg-primary-alt col-span-2"
          >
            <div class="text-sm text-quaternary">VIN</div>
            <div class="text-xl font-bold text-secondary">
              {{ agreementData.vehicle.vin }}
            </div>
          </div>
        </div>

        <!-- More details Button -->
        <div class="py-4 items-center justify-center flex">
          <Button
            severity="default"
            :label="showFullDetails ? 'Hide Details' : 'More Details'"
            text
            :icon="showFullDetails ? 'chevron-up' : 'chevron-down'"
            icon-pos="right"
            @click="$emit('toggle-full-details')"
          />
        </div>
      </Card>
    </div>

    <!-- Latest Activity -->
    <Card>
      <CardBody>
        <AgreementTimeline
          :items="localRecentTimelineData"
          :agreement-i-d="agreementID"
          @update:items="handleTimelineUpdate"
          @note-added="$emit('note-added', $event)"
          @edit-note="$emit('edit-note', $event)"
        />
        <div class="mt-2 flex justify-center">
          <Button
            plain
            severity="secondary"
            icon-pos="right"
            label="View all activity"
            :router-link="`/agreement/${agreementID}/activity`"
          />
        </div>
      </CardBody>
    </Card>

    <div class="grid grid-cols-1 gap-2">
      <!-- Appointments -->
      <Card>
        <CardBody>
          <AgreementAppointments
            :appointments-list="appointmentsData ?? undefined"
            compact
            :future-only="false"
          />
          <div class="mt-2 flex justify-center">
            <Button
              v-if="appointmentsData && appointmentsData.length > 0"
              plain
              severity="secondary"
              icon-pos="right"
              label="View all appointments"
              :router-link="`/agreement/${agreementID}/appointments`"
            />
          </div>
        </CardBody>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
// composables
import { ref, computed } from 'vue'
// components
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import Icon from '@/components/icon/Icon.vue'
import Chip from '@/components/chip/Chip.vue'
import Button from '@/components/button/Button.vue'
import CopyText from '@/components/CopyText.vue'
import LicensePlate from '@/components/license-plate/LicensePlate.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import MileageTracker from '@/components/unique/trackers/MileageTracker.vue'
import TermTracker from '@/components/unique/trackers/TermTracker.vue'
import AgreementTimeline from '@/components/unique/agreement/AgreementTimeline.vue'
import AgreementAppointments from '@/components/unique/agreement/AgreementAppointments.vue'
// utilities
import dayjs from 'dayjs'
import { formatAbbreviatedNumber, formatNumber } from '@/utilities/number'
// types
import type { Agreement } from '@/types/domain/agreement'

interface Props {
  agreementData: Agreement
  agreementID: string | number
  recentTimelineData: Array<Record<string, unknown>>
  appointmentsData: Array<Record<string, unknown>> | null
  showFullDetails: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'open-update-mileage-modal': []
  'toggle-full-details': []
  'note-added': [item: Record<string, unknown>]
  'edit-note': [item: Record<string, unknown>]
  'update:recentTimelineData': [items: Array<Record<string, unknown>>]
}>()

const isVehicleImageLoaded = ref(false)

// Local computed for timeline data to allow v-model
const localRecentTimelineData = computed({
  get: () => props.recentTimelineData,
  set: value => {
    emit('update:recentTimelineData', value)
  }
})

function handleTimelineUpdate(items: Array<Record<string, unknown>>) {
  emit('update:recentTimelineData', items)
}
</script>
